import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { REACT_APP_BASE_URL } from "src/helpers/constants";

const TOKEN_ERROR_PATTERNS = [
  "token not valid",
  "token is invalid or expired",
  "invalid token",
  "token_not_valid",
  "given token not valid",
];

const refreshClient = axios.create({
  baseURL: REACT_APP_BASE_URL,
});

const AUTH_UPDATE_EVENT = "hanypay:auth-updated";
const SESSION_EXPIRED_EVENT = "hanypay:session-expired";
const SESSION_EXPIRED_KEY = "hanypay:session-expired";

let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function clearTokens() {
  Cookies.remove("access");
  Cookies.remove("refresh");
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  localStorage.removeItem("user");
  localStorage.removeItem("userData");
  delete axios.defaults.headers.common.Authorization;
}

function invalidateSession(reason: string) {
  clearTokens();
  openSessionExpiredCircuit(reason);
}

function isSessionExpiredCircuitOpen() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
}

function openSessionExpiredCircuit(reason: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1") {
    return;
  }

  window.sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { reason },
    }),
  );
}

function closeSessionExpiredCircuit() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
}

function normalizeToken(token?: string | null) {
  return token?.replace(/^Bearer\s+/i, "").trim() || "";
}

function isValidJwt(token?: string | null) {
  const normalized = normalizeToken(token);

  if (!normalized || normalized === "undefined" || normalized === "null") {
    return false;
  }

  try {
    jwtDecode(normalized);
    return true;
  } catch {
    return false;
  }
}

function persistTokens(accessToken: string, refreshToken?: string) {
  closeSessionExpiredCircuit();
  Cookies.set("access", accessToken);
  localStorage.setItem("access", accessToken);

  if (refreshToken) {
    Cookies.set("refresh", refreshToken);
    localStorage.setItem("refresh", refreshToken);
  }

  try {
    const storedUser = localStorage.getItem("user");
    const parsedUser = storedUser ? JSON.parse(storedUser) : {};

    if (parsedUser && typeof parsedUser === "object") {
      parsedUser.access = accessToken;
      if (refreshToken) {
        parsedUser.refresh = refreshToken;
      }
      localStorage.setItem("user", JSON.stringify(parsedUser));
    } else {
      localStorage.setItem(
        "user",
        JSON.stringify({
          access: accessToken,
          refresh: refreshToken ?? localStorage.getItem("refresh") ?? "",
        }),
      );
    }
  } catch (error) {
    localStorage.setItem(
      "user",
      JSON.stringify({
        access: accessToken,
        refresh: refreshToken ?? "",
      }),
    );
  }

  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_UPDATE_EVENT, {
        detail: {
          access: accessToken,
          refresh: refreshToken ?? localStorage.getItem("refresh") ?? "",
        },
      }),
    );
  }
}

function getStoredRefreshTokenCandidate() {
  try {
    const storedUser = localStorage.getItem("user");
    const parsedUser = storedUser ? JSON.parse(storedUser) : {};
    return typeof parsedUser?.refresh === "string" ? parsedUser.refresh : "";
  } catch {
    return "";
  }
}

function subscribeToRefresh(callback: (token: string | null) => void) {
  refreshSubscribers.push(callback);
}

function notifyRefreshSubscribers(token: string | null) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function getStoredRefreshToken() {
  if (isSessionExpiredCircuitOpen()) {
    return "";
  }

  const candidates = [
    localStorage.getItem("refresh"),
    getStoredRefreshTokenCandidate(),
    Cookies.get("refresh"),
  ];

  const validToken = candidates.map((candidate) => normalizeToken(candidate)).find(isValidJwt);

  if (validToken) {
    return validToken;
  }

  return normalizeToken(localStorage.getItem("refresh") || Cookies.get("refresh"));
}

/** Whether the failed request authenticated with a merchant API key.
 *
 * Those endpoints answer 401 about the *API key*, not the user's session --
 * selecting a key the caller cannot use is a normal, recoverable error. Left
 * to the handler below it would refresh the session, retry, fail again and
 * sign the user out of the dashboard, which is how looking at the API keys
 * page could end the session.
 */
function usedMerchantApiKey(error: AxiosError<any>) {
  const headers = (error.config?.headers || {}) as Record<string, unknown>;
  return Object.keys(headers).some((name) => name.toLowerCase() === "hanypay-api-key");
}

function isTokenAuthError(error: AxiosError<any>) {
  if (usedMerchantApiKey(error)) {
    return false;
  }

  const status = error.response?.status;
  const data = error.response?.data;
  const values = [data?.detail, data?.code, data?.message, JSON.stringify(data || "")].filter(
    Boolean,
  );
  const message = values.join(" ").toLowerCase();

  return status === 401 || TOKEN_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    const originalRequest = error.config as any;

    if (isSessionExpiredCircuitOpen()) {
      return Promise.reject(error);
    }

    if (!isTokenAuthError(error) || !originalRequest) {
      return Promise.reject(error);
    }

    const refreshToken = getStoredRefreshToken();

    if (!refreshToken) {
      invalidateSession("no_refresh_token");
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      // The request already carried a freshly refreshed token. If it still
      // fails, the 401 is about something other than the session -- clearing
      // the tokens here signs the user out over an unrelated permission error.
      const data = error.response?.data;
      const message = [data?.detail, data?.code, data?.message, JSON.stringify(data || "")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (TOKEN_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
        invalidateSession("retry_failed");
      }

      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeToRefresh((token) => {
          if (!token) {
            reject(error);
            return;
          }

          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${token}`,
          };
          resolve(axios(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return refreshClient
      .post("/auth/token/refresh/", {
        refresh: refreshToken,
      })
      .then((response) => {
        const accessToken = response.data?.access;
        const nextRefreshToken = response.data?.refresh || refreshToken;

        if (!accessToken) {
          throw new Error("No access token returned from refresh endpoint");
        }

        persistTokens(accessToken, nextRefreshToken);
        notifyRefreshSubscribers(accessToken);

        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${accessToken}`,
        };

        return axios(originalRequest);
      })
      .catch((refreshError) => {
        notifyRefreshSubscribers(null);
        invalidateSession("refresh_failed");
        return Promise.reject(refreshError);
      })
      .finally(() => {
        isRefreshing = false;
      });
  },
);

export {};
